import React, { ReactNode } from 'react';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  Theme,
} from '@mui/material';
import * as locales from '@mui/material/locale';
import * as gridLocales from '@mui/x-data-grid-pro/locales';
import * as pickersLocales from '@mui/x-date-pickers/locales';

export type ThemeProviderProps = {
  /**
   * a custom MUI theme object. If none is supplied, will use default theme objet
   */
  theme?: Theme;
  children?: ReactNode;
  locale?: SupportedLocales;
};

export enum Colors {
  gray = '#000000de',
  darkGray = '#3a3a3a',
  lightGray = '#00000094',
  bgGray = '#f7f7f7',
  bgGrayHover = '#ededed',
  green = '#31833e',
  darkGreen = '#2a6f35',
  lightGreen = '#389446',
  blue = '#007da3',
  darkBlue = '#006a8a',
  lightBlue = '#0091bd',
  red = '#da310b',
  darkRed = '#b32a0a',
  lightRed = '#e3350d',
  orange = '#d14200',
  darkOrange = '#b83a00',
  lightOrange = '#eb4a00',
  yellow = '#e6bc2f',
  darkYellow = '#daa31c',
  lightYellow = '#e6bc2f',
  white = '#ffffff',
}

export const defaultTheme = createTheme({
  palette: {
    text: {
      primary: Colors.darkGray,
      secondary: Colors.gray,
      disabled: Colors.lightGray,
    },
    primary: {
      main: Colors.green,
      dark: Colors.darkGreen,
      light: Colors.lightGreen,
      contrastText: Colors.white,
    },
    secondary: {
      main: Colors.blue,
      dark: Colors.darkBlue,
      light: Colors.lightBlue,
      contrastText: Colors.white,
    },
    error: {
      main: Colors.red,
      dark: Colors.darkRed,
      light: Colors.lightRed,
      contrastText: Colors.white,
    },
    warning: {
      main: Colors.orange,
      dark: Colors.darkOrange,
      light: Colors.lightOrange,
      contrastText: Colors.white,
    },
    info: {
      main: Colors.yellow,
      dark: Colors.darkYellow,
      light: Colors.lightYellow,
      contrastText: Colors.white,
    },
    success: {
      main: Colors.green,
      dark: Colors.darkGreen,
      light: Colors.green,
      contrastText: Colors.white,
    },
  },
  typography: {
    fontFamily: 'Flexo',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Flexo';
          src: url(~/public/fonts/flexo-regular.woff) format('woff');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Flexo';
          src: url(~/public/fonts/flexo-bold.woff) format('woff');
          font-weight: 500;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'Flexo';
          src: url(~/public/fonts/flexo-heavy.woff) format('woff');
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }
        `,
    },
  },
});

type SupportedLocales = keyof typeof locales;

export function ThemeProvider({
  theme = defaultTheme,
  children,
  locale = 'enUS',
}: ThemeProviderProps) {
  const themeWithLocale = React.useMemo(
    () =>
      createTheme(
        theme,
        locales[locale],
        gridLocales[locale as keyof typeof gridLocales],
        pickersLocales[locale as keyof typeof pickersLocales]
      ),
    [locale, theme]
  );

  return (
    <>
      {/* 
      // @ts-ignore */}
      <MuiThemeProvider theme={themeWithLocale}>{children}</MuiThemeProvider>
    </>
  );
}
